import { MenuItem, PrimeIcons } from "primeng/api";
import { environment } from "../../../environments/environment";
import { PERMISSION_CODE, PermissionCode, TrackerArea } from "../interfaces/lookup.interface";
import { UserPermission } from "./permission.constants.enum";

export interface NavigationItem extends MenuItem {
    shortTitle? : string;
    items?: NavigationItem[];
    visibleTo? : UserPermission[];
    pageTitle?: string;
    helpIndex?: number;
    showFacility?: boolean;

    // The two facility-permission gates. Both are a second, independent axis from visibleTo, which
    // gates on Entra group membership - an item must satisfy every gate it declares.
    //
    // requiresTracker hides the item unless the user holds IsRead on this Tracker (OMT / SCT) at
    // the *selected* facility, so the two Tracker items follow the facility selector.
    //
    // requiresPermission hides it unless they hold IsRead on that permission at *any* facility.
    // That is the right shape for Reports and Administration: those pages have no facility
    // selector, so gating them on one the user cannot see would be unexplainable.
    //
    // Set one or the other, not both. A new facility-scoped area wants requiresTracker; anything
    // else wants requiresPermission.
    requiresTracker?: TrackerArea;
    requiresPermission?: PermissionCode;
}

// Optional external link base URL
const UI_BASE_URL = environment.externalBaseUrl;

export const NAVIGATION_ITEMS: NavigationItem[] = [
    {
        label: 'Order Management',
        shortTitle: 'Order',
        visibleTo: [UserPermission.SchedulingOrderMgmt],
        items: [
            {
                label: 'Tracker',
                title: 'Order Management Tracker for',
                icon: PrimeIcons.LIST,
                routerLink: 'order-management/tracker',
                visibleTo: [UserPermission.SchedulingOrderMgmt],
                requiresTracker: TrackerArea.ORDER_MANAGEMENT,
                pageTitle: 'Tracker - Orders',
                helpIndex: 1,
                showFacility: true
            }
        ]
    },
    {
        label: 'Scheduling',
        shortTitle: 'Sched',
        visibleTo: [UserPermission.SchedulingOrderMgmt],
        items: [
            {
                label: 'Tracker',
                title: 'Scheduling Tracker for',
                icon: PrimeIcons.LIST,
                routerLink: 'scheduling/tracker',
                visibleTo: [UserPermission.SchedulingOrderMgmt],
                requiresTracker: TrackerArea.SCHEDULING,
                pageTitle: 'Tracker - Scheduling',
                helpIndex: 2,
                showFacility: true
            }
        ]
    },
    {
        label: 'Reports',
        shortTitle: 'Reports',
        visibleTo: [UserPermission.SchedulingOrderMgmt],
        items: [
            {
                label: 'Reports',
                title: 'Reports',
                icon: PrimeIcons.FILE_EXPORT,
                routerLink: 'reports',
                visibleTo: [UserPermission.SchedulingOrderMgmt],
                requiresPermission: PERMISSION_CODE.REPORTS,
                pageTitle: 'Reports',
                helpIndex: 3
            },
        ]
    },
    {
        label: 'Administration',
        shortTitle: 'Admin',
        visibleTo: [UserPermission.SchedulingOrderMgmt],
        items: [
            {
                label: 'Facilities',
                title: 'Facilities',
                icon: PrimeIcons.DATABASE,
                routerLink: 'administration/facilities',
                visibleTo: [UserPermission.SchedulingOrderMgmt],
                requiresPermission: PERMISSION_CODE.ADMINISTRATION,
                pageTitle: 'Facilities - Administration',
            },
            {
                label: 'Statuses',
                title: 'Statuses',
                icon: PrimeIcons.DATABASE,
                routerLink: 'administration/statuses',
                visibleTo: [UserPermission.SchedulingOrderMgmt],
                requiresPermission: PERMISSION_CODE.ADMINISTRATION,
                pageTitle: 'Statuses - Administration',
            },
            {
                label: 'Priorities',
                title: 'Priorities',
                icon: PrimeIcons.DATABASE,
                routerLink: 'administration/priorities',
                visibleTo: [UserPermission.SchedulingOrderMgmt],
                requiresPermission: PERMISSION_CODE.ADMINISTRATION,
                pageTitle: 'Priorities - Administration',
            },
            {
                label: 'CPT Codes',
                title: 'CPT Codes',
                icon: PrimeIcons.DATABASE,
                routerLink: 'administration/cpt-codes',
                visibleTo: [UserPermission.SchedulingOrderMgmt],
                requiresPermission: PERMISSION_CODE.ADMINISTRATION,
                pageTitle: 'CPT Codes - Administration',
            },
            {
                label: 'Diagnosis',
                title: 'Diagnosis',
                icon: PrimeIcons.DATABASE,
                routerLink: 'administration/diagnosis',
                visibleTo: [UserPermission.SchedulingOrderMgmt],
                requiresPermission: PERMISSION_CODE.ADMINISTRATION,
                pageTitle: 'Diagnosis - Administration',
            },
            {
                label: 'Users',
                title: 'Manage Users',
                icon: PrimeIcons.USER,
                routerLink: 'administration/users',
                visibleTo: [UserPermission.SchedulingOrderMgmt],
                requiresPermission: PERMISSION_CODE.ADMINISTRATION,
                pageTitle: 'Users - Administration',
            },
            {
                label: 'Online Help',
                title: 'Online Help',
                icon: PrimeIcons.QUESTION_CIRCLE,
                routerLink: 'administration/online-help',
                visibleTo: [UserPermission.SchedulingOrderMgmt],
                requiresPermission: PERMISSION_CODE.ADMINISTRATION,
                pageTitle: 'Online Help - Administration',
            },
            {
                label: 'Downtime',
                title: 'Application Downtime',
                icon: PrimeIcons.EYE_SLASH,
                routerLink: 'administration/downtime',
                visibleTo: [UserPermission.SchedulingOrderMgmt],
                requiresPermission: PERMISSION_CODE.ADMINISTRATION,
                pageTitle: 'Downtime - Administration',
            },
        ]
    }
];

export enum SidebarState {
    EXPAND = 'Expand',
    COLLAPSE = 'Collapse'
}

export const COLLAPSED = 'collapsed';
